import React from "react";
import LogoPic from "images/logo.png";
import { Disclosure } from "@headlessui/react";
import { NavLink, useNavigate } from "react-router-dom";
import { NavItemType } from "./NavigationItem";
import { NAVIGATION_DEMO } from "data/navigation";
import SocialsList from "shared/SocialsList/SocialsList";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { useData } from "data/data-provider";
import { globalJson } from "global/global_json";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { formatPhoneLabel } from "utils/PhoneFormat";

export interface NavMobileProps {
  data?: NavItemType[];
  onClickClose?: () => void;
}

const NavMobile: React.FC<NavMobileProps> = ({
  data = NAVIGATION_DEMO,
  onClickClose,
}) => {
  const { ourCompany } = useData();
  const _renderMenuChild = (item: NavItemType) => {
    return (
      <ul className="nav-mobile-sub-menu pl-6 pb-1 text-base">
        {item.children?.map((i, index) => (
          <Disclosure key={index} as="li">
            <NavLink
              end
              to={{
                pathname: i.href || undefined,
              }}
              className={({ isActive }) =>
                `flex px-4 text-neutral-900 dark:text-neutral-200 text-sm font-medium rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 mt-0.5 ${
                  isActive ? "text-secondary" : ""
                }`
              }
            >
              <span
                className={`py-2.5 pr-3 ${!i.children ? "block w-full" : ""}`}
              >
                {i.name}
              </span>
              {i.children && (
                <span
                  className="flex-1 flex"
                  onClick={(e) => e.preventDefault()}
                >
                  <Disclosure.Button
                    as="span"
                    className="py-2.5 flex justify-end flex-1"
                  >
                    <ChevronDownIcon
                      className="ml-2 h-4 w-4 text-neutral-500"
                      aria-hidden="true"
                    />
                  </Disclosure.Button>
                </span>
              )}
            </NavLink>
            {i.children && (
              <Disclosure.Panel>{_renderMenuChild(i)}</Disclosure.Panel>
            )}
          </Disclosure>
        ))}
      </ul>
    );
  };

  return (
    <div
      className="w-full h-screen flex flex-col overflow-hidden animate-fadein"
      style={{
        background: "linear-gradient(180deg, #222222 0%, #333333 100%)",
      }}
    >
      <style>{`
              @keyframes fadein {
                from { opacity: 0; transform: translateX(48px); }
                to { opacity: 1; transform: translateX(0); }
              }
              .animate-fadein {
                animation: fadein 0.5s cubic-bezier(0.4,0,0.2,1);
              }
            `}</style>
      <div className="flex items-center justify-between px-4 py-2">
        <div>
          <img
            className={`w-28 sm:w-[200px] h-[50px] md:h-[80px] lg:h-[100px] object-contain`}
            // style={{ width: width ? `${width}px` : undefined }}
            // height={60}
            src={LogoPic}
            alt="logo"
          />
        </div>
        <div className="cursor-pointer items-start mr-4" onClick={onClickClose}>
          <svg
            width="42"
            height="42"
            viewBox="0 0 42 42"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M21 37.625C28.837 37.625 32.7556 37.625 35.1904 35.1904C37.625 32.7556 37.625 28.837 37.625 21C37.625 13.1629 37.625 9.24434 35.1904 6.80967C32.7556 4.375 28.837 4.375 21 4.375C13.1629 4.375 9.24436 4.375 6.80967 6.80967C4.375 9.24436 4.375 13.1629 4.375 21C4.375 28.837 4.375 32.7556 6.80967 35.1904C9.24434 37.625 13.1629 37.625 21 37.625Z"
              stroke="#A9A9A9"
              strokeWidth="2.625"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M26.25 15.75L15.75 26.2493M26.25 26.25L15.75 15.7507"
              stroke="#A9A9A9"
              strokeWidth="2.625"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      <div className="flex flex-col md:flex-row overflow-auto items-center md:items-stretch justify-between h-full">
        <div className="w-full md:w-1/2 px-8 lg:px-12 py-4 flex flex-col justify-center h-full">
          <ul className="flex flex-col space-y-6 justify-center h-full">
            {data.map((item, idx) => (
              <li key={idx} className="mob-m-top-16">
                {!item.children ? (
                  <NavLink
                    to={item.href || "#"}
                    className={({ isActive }) =>
                      `group relative inline-block mob-text-28 text-[36px] font-semibold tracking-wide leading-tight px-2 py-1 transition-colors duration-200 ${
                        isActive
                          ? "!font-semibold border-b-2"
                          : "text-[#A9A9A9]"
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {item.name}
                        {!isActive && (
                          <span className="absolute left-0 bottom-0 w-full h-[2px] pointer-events-none overflow-hidden">
                            <span className="block mx-auto w-0 group-hover:w-full transition-all duration-300 bg-[#A9A9A9] h-full origin-center"></span>
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                ) : (
                  <Disclosure>
                    {({ open }) => (
                      <>
                        <Disclosure.Button className="flex w-full space-x-2 mob-text-28 text-4xl font-semibold tracking-wide leading-tight text-[#A9A9A9]  items-center">
                          <span className="px-2">{item.name}</span>
                          <svg
                            width="14"
                            height="23"
                            viewBox="0 0 14 23"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className={` ${open ? "rotate-90" : ""}`}
                          >
                            <path
                              d="M13.2031 10.3594L3.35156 0.457031C2.84375 0 2.08203 0 1.625 0.457031L0.457031 1.625C0 2.08203 0 2.84375 0.457031 3.35156L8.27734 11.2227L0.457031 19.043C0 19.5508 0 20.3125 0.457031 20.7695L1.625 21.9375C2.08203 22.3945 2.84375 22.3945 3.35156 21.9375L13.2031 12.0352C13.6602 11.5781 13.6602 10.8164 13.2031 10.3594Z"
                              fill="#A9A9A9"
                            />
                          </svg>
                        </Disclosure.Button>
                        <Disclosure.Panel className="mt-3 ml-4">
                          <ul className="flex flex-col space-y-2 gap-y-3">
                            {item.children?.map((child, childIdx) => (
                              <li key={childIdx}>
                                <NavLink
                                  to={child.href || "#"}
                                  className={({ isActive }) =>
                                    `group relative text-lg font-semibold inline-block ${
                                      isActive
                                        ? "!font-semibold border-b-2"
                                        : "text-[#A9A9A9]"
                                    }`
                                  }
                                >
                                  {({ isActive }) => (
                                    <span className="relative inline-block">
                                      <span className="mb-2">{child.name}</span>
                                      {!isActive && (
                                        <span className="absolute left-0 bottom-0 w-full h-[2px] pointer-events-none overflow-hidden">
                                          <span className="block mx-auto w-0 group-hover:w-full transition-all duration-300 bg-[#A9A9A9] h-full origin-center"></span>
                                        </span>
                                      )}
                                    </span>
                                  )}
                                </NavLink>
                              </li>
                            ))}
                          </ul>
                        </Disclosure.Panel>
                      </>
                    )}
                  </Disclosure>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div
          className="hidden md:flex items-center justify-center relative"
          style={{ minWidth: 1 }}
        >
          <div
            className="w-px bg-[#FFFFFF80] absolute"
            style={{
              height: "60%",
              minHeight: 250,
              maxHeight: 400,
              top: "31%",
            }}
          ></div>
        </div>

        <div className="hidden md:flex md:w-1/2 flex-col px-8 lg:px-12 py-10 text-white md:justify-center">
          <div className="space-y-10">
            <div>
              <div className="text-2xl font-medium mb-3">Office</div>
              <div className="flex items-center space-x-3">
                <svg
                  width="14"
                  height="16"
                  viewBox="0 0 14 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13.625 15C13.8125 15 14 15.1875 14 15.375V16H0V15.375C0 15.1875 0.15625 15 0.375 15H1V0.75C1 0.34375 1.3125 0 1.75 0H12.25C12.6562 0 13 0.34375 13 0.75V15H13.625ZM4 2.375V3.625C4 3.84375 4.15625 4 4.375 4H5.625C5.8125 4 6 3.84375 6 3.625V2.375C6 2.1875 5.8125 2 5.625 2H4.375C4.15625 2 4 2.1875 4 2.375ZM4 5.375V6.625C4 6.84375 4.15625 7 4.375 7H5.625C5.8125 7 6 6.84375 6 6.625V5.375C6 5.1875 5.8125 5 5.625 5H4.375C4.15625 5 4 5.1875 4 5.375ZM5.625 10C5.8125 10 6 9.84375 6 9.625V8.375C6 8.1875 5.8125 8 5.625 8H4.375C4.15625 8 4 8.1875 4 8.375V9.625C4 9.84375 4.15625 10 4.375 10H5.625ZM8 15V12.375C8 12.1875 7.8125 12 7.625 12H6.375C6.15625 12 6 12.1875 6 12.375V15H8ZM10 9.625V8.375C10 8.1875 9.8125 8 9.625 8H8.375C8.15625 8 8 8.1875 8 8.375V9.625C8 9.84375 8.15625 10 8.375 10H9.625C9.8125 10 10 9.84375 10 9.625ZM10 6.625V5.375C10 5.1875 9.8125 5 9.625 5H8.375C8.15625 5 8 5.1875 8 5.375V6.625C8 6.84375 8.15625 7 8.375 7H9.625C9.8125 7 10 6.84375 10 6.625ZM10 3.625V2.375C10 2.1875 9.8125 2 9.625 2H8.375C8.15625 2 8 2.1875 8 2.375V3.625C8 3.84375 8.15625 4 8.375 4H9.625C9.8125 4 10 3.84375 10 3.625Z"
                    fill="#B22222"
                  />
                </svg>

                <div className="text-md text-[#A9A9A9]">
                  {ourCompany?.company?.address || globalJson.address}
                </div>
              </div>
            </div>
            <div>
              <div className="text-2xl font-medium mb-3">Contact</div>
              <div className="flex items-start space-x-3 mb-2">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15.5312 11.3125C15.7812 11.4375 15.9688 11.7188 15.9688 12.0312C15.9688 12.0625 15.9688 12.125 15.9688 12.1875L15.2188 15.4375C15.125 15.7812 14.8438 16 14.5 16C6.46875 16 0 9.53125 0 1.5C0 1.15625 0.21875 0.875 0.5625 0.78125L3.8125 0.03125C3.875 0.03125 3.9375 0 3.96875 0C4.28125 0 4.5625 0.1875 4.6875 0.46875L6.1875 3.96875C6.21875 4.0625 6.25 4.15625 6.25 4.25C6.25 4.5 6.125 4.71875 5.96875 4.84375L4.0625 6.40625C5.21875 8.84375 7.15625 10.7812 9.59375 11.9375L11.1562 10.0312C11.2812 9.875 11.5 9.75 11.7188 9.75C11.8438 9.75 11.9375 9.78125 12.0312 9.8125L15.5312 11.3125Z"
                    fill="#B22222"
                  />
                </svg>

                <div className="text-md text-[#A9A9A9]">
                  {formatPhoneLabel(
                    ourCompany?.company?.phone_no || globalJson.phone,
                  )}
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M16 14.5C16 15.3438 15.3125 16 14.5 16H1.5C0.65625 16 0 15.3438 0 14.5V6.28125C0 5.8125 0.21875 5.375 0.5625 5.09375C1.34375 4.5 1.96875 4 5.6875 1.3125C6.21875 0.9375 7.25 0 8 0.03125C8.71875 0 9.75 0.9375 10.2812 1.3125C14 4 14.625 4.5 15.4062 5.09375C15.75 5.375 16 5.8125 16 6.28125V14.5ZM13.9375 8.375C13.8438 8.25 13.6875 8.21875 13.5625 8.3125C12.875 8.84375 11.8438 9.59375 10.2812 10.7188C9.75 11.0938 8.71875 12.0312 8 12C7.25 12.0312 6.21875 11.0938 5.6875 10.7188C4.125 9.59375 3.09375 8.84375 2.40625 8.3125C2.28125 8.21875 2.125 8.25 2.03125 8.375L1.75 8.78125C1.71875 8.8125 1.71875 8.875 1.71875 8.9375C1.71875 9 1.75 9.09375 1.8125 9.125C2.53125 9.65625 3.53125 10.4062 5.09375 11.5312C5.75 12 6.875 13.0312 8 13C9.09375 13.0312 10.2188 12 10.875 11.5312C12.4375 10.4062 13.4375 9.65625 14.1562 9.125C14.2188 9.09375 14.25 9 14.25 8.9375C14.25 8.875 14.25 8.8125 14.2188 8.78125L13.9375 8.375Z"
                    fill="#B22222"
                  />
                </svg>

                <div className="text-md text-[#A9A9A9]">
                  {ourCompany?.company?.email || globalJson.email}
                </div>
              </div>
            </div>
            <div>
              <div className="text-xl font-medium mb-3">Social</div>
              <div className="flex space-x-3 mt-2">
                <a
                  href={globalJson.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 40 40"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="40" height="40" rx="8" fill="#B22222" />
                    <path
                      d="M29.8469 20.5C29.8469 25.3438 26.2922 29.3672 21.6438 30.0703V23.3125H23.9094L24.3391 20.5H21.6438V18.7031C21.6438 17.9219 22.0344 17.1797 23.2453 17.1797H24.4563V14.7969C24.4563 14.7969 23.3625 14.6016 22.2688 14.6016C20.0813 14.6016 18.6359 15.9688 18.6359 18.3906V20.5H16.175V23.3125H18.6359V30.0703C13.9875 29.3672 10.4719 25.3438 10.4719 20.5C10.4719 15.1484 14.8078 10.8125 20.1594 10.8125C25.5109 10.8125 29.8469 15.1484 29.8469 20.5Z"
                      fill="white"
                    />
                  </svg>
                </a>
                <a
                  href={globalJson.x}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 40 40"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="40" height="40" rx="8" fill="#B22222" />
                    <path
                      d="M11.0303 28.9502L18.5787 21.4018M18.5787 21.4018L11.0303 10.9502H16.0303L21.4819 18.4986M18.5787 21.4018L24.0303 28.9502H29.0303L21.4819 18.4986M29.0303 10.9502L21.4819 18.4986"
                      stroke="white"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </a>
                <a
                  href={globalJson.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 40 40"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="40" height="40" rx="8" fill="#B22222" />
                    <path
                      d="M20.1516 16.0078C22.6125 16.0078 24.6438 18.0391 24.6438 20.5C24.6438 23 22.6125 24.9922 20.1516 24.9922C17.6516 24.9922 15.6594 23 15.6594 20.5C15.6594 18.0391 17.6516 16.0078 20.1516 16.0078ZM20.1516 23.4297C21.7531 23.4297 23.0422 22.1406 23.0422 20.5C23.0422 18.8984 21.7531 17.6094 20.1516 17.6094C18.5109 17.6094 17.2219 18.8984 17.2219 20.5C17.2219 22.1406 18.55 23.4297 20.1516 23.4297ZM25.8547 15.8516C25.8547 16.4375 25.3859 16.9062 24.8 16.9062C24.2141 16.9062 23.7453 16.4375 23.7453 15.8516C23.7453 15.2656 24.2141 14.7969 24.8 14.7969C25.3859 14.7969 25.8547 15.2656 25.8547 15.8516ZM28.8234 16.9062C28.9016 18.3516 28.9016 22.6875 28.8234 24.1328C28.7453 25.5391 28.4328 26.75 27.4172 27.8047C26.4016 28.8203 25.1516 29.1328 23.7453 29.2109C22.3 29.2891 17.9641 29.2891 16.5188 29.2109C15.1125 29.1328 13.9016 28.8203 12.8469 27.8047C11.8313 26.75 11.5188 25.5391 11.4406 24.1328C11.3625 22.6875 11.3625 18.3516 11.4406 16.9062C11.5188 15.5 11.8313 14.25 12.8469 13.2344C13.9016 12.2188 15.1125 11.9062 16.5188 11.8281C17.9641 11.75 22.3 11.75 23.7453 11.8281C25.1516 11.9062 26.4016 12.2188 27.4172 13.2344C28.4328 14.25 28.7453 15.5 28.8234 16.9062ZM26.9484 25.6562C27.4172 24.5234 27.3 21.7891 27.3 20.5C27.3 19.25 27.4172 16.5156 26.9484 15.3438C26.6359 14.6016 26.05 13.9766 25.3078 13.7031C24.1359 13.2344 21.4016 13.3516 20.1516 13.3516C18.8625 13.3516 16.1281 13.2344 14.9953 13.7031C14.2141 14.0156 13.6281 14.6016 13.3156 15.3438C12.8469 16.5156 12.9641 19.25 12.9641 20.5C12.9641 21.7891 12.8469 24.5234 13.3156 25.6562C13.6281 26.4375 14.2141 27.0234 14.9953 27.3359C16.1281 27.8047 18.8625 27.6875 20.1516 27.6875C21.4016 27.6875 24.1359 27.8047 25.3078 27.3359C26.05 27.0234 26.675 26.4375 26.9484 25.6562Z"
                      fill="white"
                    />
                  </svg>
                </a>
                <a
                  href={globalJson.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 40 40"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="40" height="40" rx="8" fill="#B22222" />
                    <path
                      d="M18.1121 18.6633C17.0622 18.5096 14.6691 18.7639 13.4957 21.0107C12.3222 23.2575 13.5049 25.596 14.2429 26.4843C14.9717 27.3155 17.2881 28.8894 19.7451 27.3527C20.3541 26.9718 21.1128 26.6877 21.9736 23.7114L21.8735 12C21.7074 13.2896 23.0831 16.3144 27 16.6723"
                      stroke="white"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </a>
                <a
                  href={globalJson.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 40 40"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="40" height="40" rx="8" fill="#B22222" />
                    <path
                      d="M30.3625 15.3828C30.8313 17.0234 30.8313 20.5391 30.8313 20.5391C30.8313 20.5391 30.8313 24.0156 30.3625 25.6953C30.1281 26.6328 29.3859 27.3359 28.4875 27.5703C26.8078 28 20.1672 28 20.1672 28C20.1672 28 13.4875 28 11.8078 27.5703C10.9094 27.3359 10.1672 26.6328 9.93281 25.6953C9.46406 24.0156 9.46406 20.5391 9.46406 20.5391C9.46406 20.5391 9.46406 17.0234 9.93281 15.3828C10.1672 14.4453 10.9094 13.7031 11.8078 13.4688C13.4875 13 20.1672 13 20.1672 13C20.1672 13 26.8078 13 28.4875 13.4688C29.3859 13.7031 30.1281 14.4453 30.3625 15.3828ZM17.9797 23.7031L23.5266 20.5391L17.9797 17.375V23.7031Z"
                      fill="white"
                    />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NavMobile;
